import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { TablePagination } from '@/components/molecules/TablePagination';
import { SyncFromNotionButton } from '@/features/zones/components/SyncFromNotionButton';
import { ZoneFilterBar } from '@/features/zones/components/ZoneFilterBar';
import { ZonesDashboardReadOnly } from '@/features/zones/components/ZonesDashboardReadOnly';
import { ZONE_COUNTRY_FILTER_VALUES } from '@/features/zones/constants/markets';
import { useZoneFilterOptionsQuery, useZonesQuery } from '@/features/zones/services/zones.api';
import {
  ZONE_FILTER_TRACKED_AXES,
  ZONES_MIXPANEL_EVENTS,
  type ZoneFiltersAppliedPayload,
} from '@/features/zones/utils/mixpanelEvents';
import { serializeFilterToParams } from '@/features/zones/utils/serializeFilterToParams';
import { useMixpanel } from '@/hooks/useMixpanel';
import type { IZoneListFilter, ZoneCountryFilter } from '@/types/api';

/**
 * Builds the filter object from the URL search params so the
 * Management screen's filter state is shareable + bookmarkable.
 * The inverse writer is `serializeFilterToParams`. There is no
 * `status` axis — hunting zones carry no SpotOn workflow status.
 */
function buildFilterFromParams(params: URLSearchParams): IZoneListFilter {
  const filter: IZoneListFilter = { page: 1, limit: 10 };

  const countries = params.getAll('country');
  if (countries.length > 0) {
    filter.countries = countries.filter((c): c is ZoneCountryFilter =>
      ZONE_COUNTRY_FILTER_VALUES.includes(c as ZoneCountryFilter),
    );
  }

  const names = params.getAll('name');
  if (names.length > 0) filter.names = names;

  const ownerIds = params.getAll('owner_id');
  if (ownerIds.length > 0) filter.owner_ids = ownerIds;

  const rawPage = params.get('page');
  if (rawPage !== null && /^\d+$/.test(rawPage)) {
    const parsedPage = Number.parseInt(rawPage, 10);
    filter.page = parsedPage >= 1 ? parsedPage : 1;
  } else {
    filter.page = 1;
  }

  return filter;
}

const FILTER_TRACKING_DEBOUNCE_MS = 500;

export function ZoneManagementPage() {
  const [params, setParams] = useSearchParams();
  const filter = buildFilterFromParams(params);
  const trackAction = useMixpanel((state) => state.trackAction);

  const { data, isLoading, isError, refetch, isPlaceholderData } = useZonesQuery(filter);
  // Filter options are global, not page-scoped: derived from the current page
  // they would change under the BD as they paginate. A failed request leaves the bar
  // usable — the owner and country popovers then only offer their unset option.
  const { data: filterOptions } = useZoneFilterOptionsQuery();
  const total = data?.pagination.total;
  // `totalPagesLoaded` is only defined once the first response arrives.
  // While undefined (first load) we must NOT clamp — we don't yet know the
  // real ceiling and would rewrite a legitimate deep-link (e.g. ?page=3)
  // down to page 1 before the data arrives.
  const totalPagesLoaded = data?.pagination.total_pages;
  const totalPages = totalPagesLoaded ?? 1;
  const requestedPage = filter.page ?? 1;
  const zones = data?.zones ?? [];

  const { page: _page, limit: _limit, ...filterForBar } = filter;

  // Stable ref to filterForBar so the clamp effect can read the latest value
  // without adding the derived object to its dependency array (it is recreated
  // on every render, which would cause the effect to fire continuously).
  const filterForBarRef = useRef(filterForBar);
  filterForBarRef.current = filterForBar;

  // Effective current page for rendering: clamp only once we know total_pages.
  const currentPage =
    totalPagesLoaded != null
      ? Math.min(requestedPage, Math.max(totalPagesLoaded, 1))
      : requestedPage;

  // URL normalisation: when the loaded data shows the requested page
  // is genuinely over-range, silently rewrite the URL to the clamped page with
  // REPLACE so the stale deep-link doesn't stay in history.
  // CRITICAL: the effect is a no-op until `totalPagesLoaded` is defined, which
  // prevents rewriting a valid deep-link (e.g. ?page=3) on the very first
  // render before the data arrives.
  //
  // filterForBar is read via ref to avoid adding the derived plain object
  // (re-created every render) to deps — the ref always holds the current value.
  useEffect(() => {
    if (totalPagesLoaded == null) return; // not loaded yet — do nothing
    if (requestedPage <= totalPagesLoaded) return; // in range — do nothing
    const clampedPage = Math.max(totalPagesLoaded, 1);
    setParams(serializeFilterToParams({ ...filterForBarRef.current, page: clampedPage }), {
      replace: true,
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: filterForBar read via ref to avoid re-running on every render
  }, [requestedPage, totalPagesLoaded, setParams]);

  // Ticking four countries is four onChange calls but one intent, so the emit is
  // debounced and carries the settled selection rather than each intermediate one.
  const trackTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(trackTimerRef.current), []);

  const trackFilterChange = useCallback(
    (next: Omit<IZoneListFilter, 'page' | 'limit'>) => {
      clearTimeout(trackTimerRef.current);
      trackTimerRef.current = setTimeout(() => {
        const activeAxes = ZONE_FILTER_TRACKED_AXES.filter((axis) => (next[axis] ?? []).length > 0);
        const payload: ZoneFiltersAppliedPayload = {
          active_axes: [...activeAxes],
          axis_count: activeAxes.length,
          value_count: activeAxes.reduce((total, axis) => total + (next[axis] ?? []).length, 0),
          cleared: activeAxes.length === 0,
        };
        trackAction(ZONES_MIXPANEL_EVENTS.FILTERS_APPLIED, payload);
      }, FILTER_TRACKING_DEBOUNCE_MS);
    },
    [trackAction],
  );

  function handleFilterChange(next: Omit<IZoneListFilter, 'page' | 'limit'>) {
    setParams(serializeFilterToParams(next), { replace: true });
    trackFilterChange(next);
  }

  function handlePageChange(newPage: number) {
    setParams(serializeFilterToParams({ ...filterForBar, page: newPage }));
  }

  return (
    <section className="container mx-auto mt-14 px-4 py-12">
      <header className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2">
        <h1 className="min-w-0 text-2xl font-semibold sm:text-3xl">Zone Management</h1>
        <div className="col-start-2 row-start-1 flex shrink-0 items-center gap-2">
          <SyncFromNotionButton />
        </div>
        <p className="col-span-2 hidden text-sm text-muted-foreground sm:col-span-1 sm:block">
          Read-only. Hunting zones are sourced from Notion — edit a zone in Notion and re-sync.
        </p>
      </header>

      {/* Fixed height so this row and History's equivalent spacer always match. */}
      <div className="mb-4 min-h-10" data-testid="zone-filter-row">
        <ZoneFilterBar
          filter={filterForBar}
          onChange={handleFilterChange}
          nameOptions={filterOptions?.names ?? []}
          ownerOptions={filterOptions?.owners ?? []}
          countryOptions={filterOptions?.countries ?? []}
        />
      </div>

      {(total !== undefined || totalPages > 1) && (
        // aria-live announces total/page changes to screen readers.
        <div
          aria-live="polite"
          className="mt-4 flex items-center justify-between text-sm text-muted-foreground"
        >
          <span>{totalPages > 1 && `Page ${currentPage} of ${totalPages}`}</span>
          <span>{total !== undefined && `${total} zone${total === 1 ? '' : 's'}`}</span>
        </div>
      )}

      <ZonesDashboardReadOnly
        zones={zones}
        // Suppress spurious empty-state flash on over-range pages.
        // When zones is empty but total > 0 the API returned an out-of-range page
        // (or the clamp re-fetch is in flight). Treat this as loading rather than
        // a genuine empty result so users never see "No zones yet" while zones exist.
        // Genuine empty (total === 0) falls through to the real empty-state copy.
        isLoading={isLoading || (zones.length === 0 && (total ?? 0) > 0)}
        isError={isError}
        onRetry={() => refetch()}
        isPlaceholderData={isPlaceholderData}
      />

      {totalPages > 1 && (
        <div className="mt-6">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </section>
  );
}

// react-router's route registration in `src/components/App.tsx` consumes the
// page via a default import; keep both exports so the named-export convention
// and the router wiring both hold.
export default ZoneManagementPage;
